import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import NotificationPanel from './NotificationPanel';
import type { AppDispatch, RootState } from '../../store/store';
import { fetchNotificationBadge } from '../../store/slices/notificationFeedSlice';
import bellIcon from '../../assets/icons/Icon-Header-bell.svg';

const MOBILE_BREAKPOINT = 768;
// Same cadence as Wawi bridge status on Profile (short poll, no websockets).
const NOTIFICATION_BADGE_POLL_MS = 15000;

const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const bellRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const unreadCount = useSelector((state: RootState) => state.notificationFeed.unreadCount);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isDataInitialized = useSelector((state: RootState) => state.auth.isDataInitialized);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isDataInitialized) {
      return undefined;
    }
    const tick = () => {
      if (document.hidden) {
        return;
      }
      void dispatch(fetchNotificationBadge());
    };
    tick();
    const id = window.setInterval(tick, NOTIFICATION_BADGE_POLL_MS);
    return () => window.clearInterval(id);
  }, [dispatch, isAuthenticated, isDataInitialized]);

  useEffect(() => {
    if (open && isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open, isMobile]);

  const badgeLabel =
    unreadCount > 9 ? '9+' : unreadCount > 0 ? String(unreadCount) : null;

  const toggle = () => setOpen((value) => !value);
  const close = useCallback(() => setOpen(false), []);

  const handleOpenTask = useCallback(
    (taskId: string) => {
      navigate(`/tasks?taskId=${encodeURIComponent(taskId)}`);
    },
    [navigate],
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="theme-button notif-bell-wrap" ref={bellRef}>
      <button
        type="button"
        className="theme-toggle notif-bell-toggle"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          unreadCount > 0
            ? t('notifications.bell.aria_with_count', { count: unreadCount })
            : t('notifications.bell.aria')
        }
      >
        <img src={bellIcon} alt="" className="theme-icon" aria-hidden="true" />
        {badgeLabel && (
          <span className="notif-bell-badge" aria-hidden="true">
            {badgeLabel}
          </span>
        )}
      </button>
      {!isMobile && open && (
        <div className="notif-panel-anchor">
          <NotificationPanel
            open={open}
            onClose={close}
            onOpenTask={handleOpenTask}
            isMobile={false}
            outsideIgnoreRef={bellRef}
          />
        </div>
      )}
      {isMobile && (
        <NotificationPanel
          open={open}
          onClose={close}
          onOpenTask={handleOpenTask}
          isMobile
          outsideIgnoreRef={bellRef}
        />
      )}
    </div>
  );
};

export default NotificationBell;
